import { QUERY_KEYS } from "@/libraries/constant";
import type {
	GetMessageDiscussionsSchema,
	PostReadMessagePayloadSchema,
} from "@/schemas";
import { messageService } from "@/services";
import type { GetMessageResponse, ResError } from "@/types";
import type { GetMessageTotalUnreadResponseType } from "@/types/get-message-total-unread";
import {
	type UnusedSkipTokenOptions,
	infiniteQueryOptions,
	queryOptions,
} from "@tanstack/react-query";

export const getMessagesQueryOptions = (params: GetMessageDiscussionsSchema) =>
	infiniteQueryOptions({
		queryKey: [QUERY_KEYS.GET_MESSAGES, params],
		initialPageParam: 1,
		queryFn: ({ pageParam }) =>
			messageService.discussions({ ...params, page: pageParam }),
		getNextPageParam: (lastPage) => lastPage?.meta?.next_page,
	});

export const getMessageQueryOptions = (
	id: number,
	options?: Omit<
		UnusedSkipTokenOptions<
			GetMessageResponse,
			ResError,
			GetMessageResponse,
			[string, number]
		>,
		"queryFn" | "queryKey"
	>,
) =>
	queryOptions({
		queryKey: [QUERY_KEYS.GET_MESSAGE, id],
		queryFn: () => messageService.detail(id),
		enabled: !!id && +id > 0,
		...options,
	});

export const getMessageUnreadOptions = (
	options?: Omit<
		UnusedSkipTokenOptions<
			GetMessageTotalUnreadResponseType,
			Error,
			GetMessageTotalUnreadResponseType,
			ReadonlyArray<typeof QUERY_KEYS.GET_UNREADS>
		>,
		"queryKey" | "queryFn"
	>,
) =>
	queryOptions({
		queryKey: [QUERY_KEYS.GET_UNREADS],
		queryFn: () => messageService.unread(),
		...options,
	});

export const markReadAllMessageQueryOptions = (
	payload: PostReadMessagePayloadSchema,
	options?: Omit<
		UnusedSkipTokenOptions<
			unknown,
			Error,
			unknown,
			[typeof QUERY_KEYS.GET_MARK_UNREADS, typeof payload]
		>,
		"queryKey" | "queryFn"
	>,
) =>
	queryOptions({
		queryKey: [QUERY_KEYS.GET_MARK_UNREADS, payload],
		queryFn: ({ signal }) => messageService.read(payload, { signal }),
		...options,
	});
